import React, { useState, useRef } from 'react';
import { X, Download, Share2, Palette, Sparkles, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { supabase } from '@/db/supabase';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked_at: string;
}

interface PosterGeneratorProps {
  achievement: Achievement;
  username: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type PosterTemplate = 'tech' | 'minimal' | 'colorful' | 'vintage' | 'cyberpunk' | 'inkwash';

const stickers = ['⚡', '🎉', '🏆', '⭐', '💎', '🔥', '✨', '🎯', '🚀', '💪', '👑', '🌟'];

const backgroundPresets = {
  tech: [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  ],
  minimal: [
    'linear-gradient(135deg, #fdfcfb 0%, #e2d1c3 100%)',
    'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  ],
  colorful: [
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  ],
  vintage: [
    'linear-gradient(135deg, #d4a574 0%, #8b6f47 100%)',
    'linear-gradient(135deg, #e8c4a0 0%, #c9a882 100%)',
    'linear-gradient(135deg, #f4e4d7 0%, #d4b896 100%)',
    'linear-gradient(135deg, #c9a882 0%, #a67c52 100%)',
    'linear-gradient(135deg, #e6d5c3 0%, #b89968 100%)',
    'linear-gradient(135deg, #d9c7b8 0%, #a88860 100%)',
  ],
  cyberpunk: [
    'linear-gradient(135deg, #ff006e 0%, #8338ec 100%)',
    'linear-gradient(135deg, #06ffa5 0%, #00d9ff 100%)',
    'linear-gradient(135deg, #ff0080 0%, #ff8c00 100%)',
    'linear-gradient(135deg, #00f5ff 0%, #ff00ff 100%)',
    'linear-gradient(135deg, #39ff14 0%, #ff1493 100%)',
    'linear-gradient(135deg, #ffff00 0%, #ff00ff 100%)',
    'linear-gradient(135deg, #00ffff 0%, #ff0000 100%)',
  ],
  inkwash: [
    'linear-gradient(135deg, #f5f5f5 0%, #d0d0d0 100%)',
    'linear-gradient(135deg, #e8e8e8 0%, #b8b8b8 100%)',
    'linear-gradient(135deg, #fafafa 0%, #c8c8c8 100%)',
    'linear-gradient(135deg, #f0f0f0 0%, #a8a8a8 100%)',
    'linear-gradient(135deg, #e5e5e5 0%, #b0b0b0 100%)',
    'linear-gradient(135deg, #f8f8f8 0%, #c0c0c0 100%)',
  ],
};

export const AchievementPosterGenerator: React.FC<PosterGeneratorProps> = ({
  achievement,
  username,
  open,
  onOpenChange,
}) => {
  const posterRef = useRef<HTMLDivElement>(null);
  const [template, setTemplate] = useState<PosterTemplate>('tech');
  const [background, setBackground] = useState(backgroundPresets.tech[0]);
  const [signature, setSignature] = useState('');
  const [motto, setMotto] = useState('');
  const [selectedStickers, setSelectedStickers] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);

  const handleTemplateChange = (newTemplate: PosterTemplate) => {
    setTemplate(newTemplate);
    setBackground(backgroundPresets[newTemplate][0]);
  };

  const toggleSticker = (sticker: string) => {
    if (selectedStickers.includes(sticker)) {
      setSelectedStickers(selectedStickers.filter(s => s !== sticker));
    } else if (selectedStickers.length < 5) {
      setSelectedStickers([...selectedStickers, sticker]);
    } else {
      toast.error('最多选择 5 个贴纸');
    }
  };

  const generatePoster = async () => {
    if (!posterRef.current) return;

    setGenerating(true);
    try {
      const canvas = await html2canvas(posterRef.current, {
        scale: 2,
        backgroundColor: null,
        logging: false,
      });

      const dataUrl = canvas.toDataURL('image/png');
      return dataUrl;
    } catch (error) {
      console.error('生成海报失败:', error);
      toast.error('生成海报失败');
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    const dataUrl = await generatePoster();
    if (!dataUrl) return;

    const link = document.createElement('a');
    link.download = `achievement-${achievement.name}-${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
    toast.success('海报已下载');
  };

  const handleShareToCommunity = async () => {
    const dataUrl = await generatePoster();
    if (!dataUrl) return;

    try {
      // 将 base64 转换为 Blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // 上传到 Supabase Storage
      const fileName = `achievement-${achievement.id}-${Date.now()}.png`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('achievement-posters')
        .upload(fileName, blob, { contentType: 'image/png' });

      if (uploadError) throw uploadError;

      // 获取公开 URL
      const { data: urlData } = supabase.storage
        .from('achievement-posters')
        .getPublicUrl(fileName);

      // 创建社区帖子
      const { error: postError } = await supabase.from('community_posts').insert({
        content: `🎉 我解锁了成就「${achievement.name}」！\n${achievement.description}\n${motto ? `\n💬 ${motto}` : ''}`,
        images: [urlData.publicUrl],
        tags: ['成就', achievement.name],
      });

      if (postError) throw postError;

      // 记录分享历史
      await supabase.from('achievement_shares').insert({
        achievement_id: achievement.id,
        share_image_url: urlData.publicUrl,
        share_to_community: true,
      });

      toast.success('已分享到社区');
      onOpenChange(false);
    } catch (error: any) {
      console.error('分享失败:', error);
      toast.error(error.message || '分享失败');
    }
  };

  const renderPoster = () => {
    const commonStyles = "w-full h-full flex flex-col items-center justify-center p-8 relative overflow-hidden";

    if (template === 'tech') {
      return (
        <div className={commonStyles} style={{ background }}>
          {/* 装饰元素 */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full" />
            <div className="absolute bottom-10 right-10 w-40 h-40 border-4 border-white rounded-lg rotate-45" />
          </div>

          {/* 内容 */}
          <div className="relative z-10 text-center space-y-6">
            <div className="text-8xl mb-4 animate-bounce">{achievement.icon}</div>
            <h2 className="text-4xl font-bold text-white drop-shadow-lg">{achievement.name}</h2>
            <p className="text-xl text-white/90 max-w-md">{achievement.description}</p>
            <div className="flex items-center justify-center gap-2 text-white/80 text-lg">
              <span>@{username}</span>
              {signature && <span>· {signature}</span>}
            </div>
            {motto && (
              <div className="mt-6 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                <p className="text-white text-lg italic">"{motto}"</p>
              </div>
            )}
            <div className="flex gap-3 justify-center mt-4">
              {selectedStickers.map((sticker, idx) => (
                <span key={idx} className="text-4xl animate-pulse">{sticker}</span>
              ))}
            </div>
            <div className="text-white/60 text-sm mt-8">
              {new Date(achievement.unlocked_at).toLocaleDateString('zh-CN')}
            </div>
          </div>
        </div>
      );
    }

    if (template === 'minimal') {
      return (
        <div className={commonStyles} style={{ background }}>
          <div className="relative z-10 text-center space-y-4 max-w-lg">
            <div className="text-7xl mb-6">{achievement.icon}</div>
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-gray-800">{achievement.name}</h2>
              <p className="text-lg text-gray-600">{achievement.description}</p>
            </div>
            <div className="border-t-2 border-gray-300 my-6 w-32 mx-auto" />
            <div className="text-gray-700">
              <p className="font-medium">@{username}</p>
              {signature && <p className="text-sm text-gray-500 mt-1">{signature}</p>}
            </div>
            {motto && (
              <div className="mt-6 px-4 py-2 border-l-4 border-gray-400">
                <p className="text-gray-700 italic">"{motto}"</p>
              </div>
            )}
            <div className="flex gap-2 justify-center mt-4">
              {selectedStickers.map((sticker, idx) => (
                <span key={idx} className="text-3xl">{sticker}</span>
              ))}
            </div>
            <div className="text-gray-400 text-xs mt-6">
              {new Date(achievement.unlocked_at).toLocaleDateString('zh-CN')}
            </div>
          </div>
        </div>
      );
    }

    // colorful template
    if (template === 'colorful') {
      return (
        <div className={commonStyles} style={{ background }}>
          {/* 装饰圆点 */}
          <div className="absolute inset-0 opacity-20">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-4 h-4 bg-white rounded-full"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                }}
              />
            ))}
          </div>

          {/* 内容 */}
          <div className="relative z-10 text-center space-y-6">
            <div className="text-9xl mb-4 drop-shadow-2xl">{achievement.icon}</div>
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
              <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {achievement.name}
              </h2>
              <p className="text-lg text-gray-700 mt-3">{achievement.description}</p>
              <div className="flex items-center justify-center gap-2 text-gray-600 mt-4">
                <span className="font-bold">@{username}</span>
                {signature && <span>· {signature}</span>}
              </div>
              {motto && (
                <div className="mt-4 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl">
                  <p className="text-gray-800 italic">"{motto}"</p>
                </div>
              )}
              <div className="flex gap-3 justify-center mt-4">
                {selectedStickers.map((sticker, idx) => (
                  <span key={idx} className="text-4xl">{sticker}</span>
                ))}
              </div>
              <div className="text-gray-400 text-sm mt-4">
                {new Date(achievement.unlocked_at).toLocaleDateString('zh-CN')}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // vintage template
    if (template === 'vintage') {
      return (
        <div className={commonStyles} style={{ background }}>
          {/* 纸质纹理效果 */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
            }} />
          </div>

          {/* 装饰边框 */}
          <div className="absolute inset-8 border-4 border-amber-900/30 rounded-lg" />
          <div className="absolute inset-12 border-2 border-amber-900/20" />

          {/* 内容 */}
          <div className="relative z-10 text-center space-y-4 max-w-lg">
            <div className="text-8xl mb-6 filter sepia-[0.3]">{achievement.icon}</div>
            <div className="space-y-3">
              <h2 className="text-4xl font-serif font-bold text-amber-900">{achievement.name}</h2>
              <div className="flex items-center justify-center gap-3">
                <div className="h-px w-16 bg-amber-900/40" />
                <span className="text-amber-900/60">✦</span>
                <div className="h-px w-16 bg-amber-900/40" />
              </div>
              <p className="text-lg text-amber-800/80 font-serif italic">{achievement.description}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-amber-900/20">
              <p className="text-amber-900 font-serif">@{username}</p>
              {signature && <p className="text-sm text-amber-800/70 mt-1 italic">{signature}</p>}
            </div>
            {motto && (
              <div className="mt-4 px-6 py-3 bg-amber-900/10 rounded-lg border-l-4 border-amber-900/40">
                <p className="text-amber-900 font-serif italic">"{motto}"</p>
              </div>
            )}
            <div className="flex gap-3 justify-center mt-4 filter sepia-[0.2]">
              {selectedStickers.map((sticker, idx) => (
                <span key={idx} className="text-3xl">{sticker}</span>
              ))}
            </div>
            <div className="text-amber-800/50 text-xs mt-6 font-serif">
              {new Date(achievement.unlocked_at).toLocaleDateString('zh-CN')}
            </div>
          </div>
        </div>
      );
    }

    // cyberpunk template
    if (template === 'cyberpunk') {
      return (
        <div className={commonStyles} style={{ background }}>
          {/* 网格背景 */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            }} />
          </div>

          {/* 故障效果装饰 */}
          <div className="absolute top-20 left-10 w-40 h-2 bg-cyan-400 opacity-70 blur-sm" />
          <div className="absolute bottom-20 right-10 w-32 h-2 bg-pink-500 opacity-70 blur-sm" />
          <div className="absolute top-1/2 left-5 w-2 h-40 bg-yellow-400 opacity-60 blur-sm" />

          {/* 内容 */}
          <div className="relative z-10 text-center space-y-6">
            <div className="text-9xl mb-4 drop-shadow-[0_0_30px_rgba(255,255,255,0.8)]">{achievement.icon}</div>
            <div className="bg-black/60 backdrop-blur-md rounded-2xl p-8 border-2 border-white/30 shadow-[0_0_50px_rgba(255,255,255,0.3)]">
              <h2 className="text-4xl font-bold text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.8)] tracking-wider uppercase">
                {achievement.name}
              </h2>
              <p className="text-lg text-cyan-300 mt-3 font-mono">{achievement.description}</p>
              <div className="flex items-center justify-center gap-2 text-pink-400 mt-4 font-mono">
                <span className="font-bold">@{username}</span>
                {signature && <span>· {signature}</span>}
              </div>
              {motto && (
                <div className="mt-4 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-pink-500/20 rounded-lg border border-white/20">
                  <p className="text-white font-mono italic">"{motto}"</p>
                </div>
              )}
              <div className="flex gap-3 justify-center mt-4">
                {selectedStickers.map((sticker, idx) => (
                  <span key={idx} className="text-4xl drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]">{sticker}</span>
                ))}
              </div>
              <div className="text-cyan-400/70 text-sm mt-4 font-mono">
                {new Date(achievement.unlocked_at).toLocaleDateString('zh-CN')}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // inkwash template
    if (template === 'inkwash') {
      return (
        <div className={commonStyles} style={{ background }}>
          {/* 水墨装饰元素 */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 right-10 w-48 h-48 rounded-full bg-gradient-radial from-gray-800 to-transparent blur-3xl" />
            <div className="absolute bottom-10 left-10 w-64 h-64 rounded-full bg-gradient-radial from-gray-700 to-transparent blur-3xl" />
          </div>

          {/* 中国风装饰 */}
          <div className="absolute top-8 left-8 text-gray-400/30 text-6xl">☯</div>
          <div className="absolute bottom-8 right-8 text-gray-400/30 text-6xl">☯</div>

          {/* 内容 */}
          <div className="relative z-10 text-center space-y-6 max-w-lg">
            <div className="text-8xl mb-6 filter grayscale">{achievement.icon}</div>
            <div className="space-y-4">
              <h2 className="text-4xl font-bold text-gray-800" style={{ fontFamily: 'KaiTi, STKaiti, serif' }}>
                {achievement.name}
              </h2>
              <div className="flex items-center justify-center gap-4">
                <div className="h-px w-20 bg-gradient-to-r from-transparent via-gray-400 to-transparent" />
                <span className="text-gray-500">◆</span>
                <div className="h-px w-20 bg-gradient-to-r from-transparent via-gray-400 to-transparent" />
              </div>
              <p className="text-lg text-gray-600" style={{ fontFamily: 'KaiTi, STKaiti, serif' }}>
                {achievement.description}
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-300">
              <p className="text-gray-700" style={{ fontFamily: 'KaiTi, STKaiti, serif' }}>@{username}</p>
              {signature && (
                <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'KaiTi, STKaiti, serif' }}>
                  {signature}
                </p>
              )}
            </div>
            {motto && (
              <div className="mt-4 px-6 py-3 border-l-4 border-gray-400 bg-gray-100/50">
                <p className="text-gray-700 italic" style={{ fontFamily: 'KaiTi, STKaiti, serif' }}>
                  "{motto}"
                </p>
              </div>
            )}
            <div className="flex gap-3 justify-center mt-4 filter grayscale">
              {selectedStickers.map((sticker, idx) => (
                <span key={idx} className="text-3xl">{sticker}</span>
              ))}
            </div>
            <div className="text-gray-400 text-xs mt-6" style={{ fontFamily: 'KaiTi, STKaiti, serif' }}>
              {new Date(achievement.unlocked_at).toLocaleDateString('zh-CN')}
            </div>
          </div>
        </div>
      );
    }

    // default: return tech template
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">🎨 成就海报生成器</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 左侧：预览 */}
          <div className="space-y-4">
            <Label className="text-lg font-bold">海报预览</Label>
            <div
              ref={posterRef}
              className="w-full aspect-square rounded-2xl overflow-hidden shadow-2xl"
            >
              {renderPoster()}
            </div>
          </div>

          {/* 右侧：配置 */}
          <div className="space-y-6">
            <Tabs value={template} onValueChange={(val) => handleTemplateChange(val as PosterTemplate)}>
              <TabsList className="grid w-full grid-cols-3 mb-2">
                <TabsTrigger value="tech">科技风</TabsTrigger>
                <TabsTrigger value="minimal">简约风</TabsTrigger>
                <TabsTrigger value="colorful">炫彩风</TabsTrigger>
              </TabsList>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="vintage">复古风</TabsTrigger>
                <TabsTrigger value="cyberpunk">赛博朋克</TabsTrigger>
                <TabsTrigger value="inkwash">水墨风</TabsTrigger>
              </TabsList>

              <TabsContent value={template} className="space-y-4 mt-4">
                {/* 背景选择 */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Palette className="h-4 w-4" /> 背景颜色
                  </Label>
                  <div className="grid grid-cols-4 gap-2">
                    {backgroundPresets[template].map((bg, idx) => (
                      <button
                        key={idx}
                        className="w-full h-12 rounded-lg border-2 transition-all hover:scale-105"
                        style={{
                          background: bg,
                          borderColor: background === bg ? '#000' : 'transparent',
                        }}
                        onClick={() => setBackground(bg)}
                      />
                    ))}
                  </div>
                </div>

                {/* 个人签名 */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4" /> 个人签名
                  </Label>
                  <Input
                    placeholder="输入你的个性签名..."
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    maxLength={30}
                    className="rounded-xl"
                  />
                  <p className="text-xs text-muted-foreground">{signature.length} / 30</p>
                </div>

                {/* 座右铭 */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" /> 座右铭
                  </Label>
                  <Input
                    placeholder="输入你的座右铭..."
                    value={motto}
                    onChange={(e) => setMotto(e.target.value)}
                    maxLength={50}
                    className="rounded-xl"
                  />
                  <p className="text-xs text-muted-foreground">{motto.length} / 50</p>
                </div>

                {/* 贴纸装饰 */}
                <div className="space-y-2">
                  <Label>贴纸装饰（最多 5 个）</Label>
                  <div className="flex flex-wrap gap-2">
                    {stickers.map((sticker) => (
                      <Badge
                        key={sticker}
                        variant={selectedStickers.includes(sticker) ? 'default' : 'outline'}
                        className="text-2xl cursor-pointer px-3 py-2 hover:scale-110 transition-transform"
                        onClick={() => toggleSticker(sticker)}
                      >
                        {sticker}
                      </Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* 操作按钮 */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                className="flex-1 rounded-xl h-11 font-bold"
                onClick={handleDownload}
                disabled={generating}
              >
                <Download className="h-4 w-4 mr-2" />
                {generating ? '生成中...' : '下载海报'}
              </Button>
              <Button
                className="flex-1 rounded-xl h-11 font-bold"
                variant="outline"
                onClick={handleShareToCommunity}
                disabled={generating}
              >
                <Share2 className="h-4 w-4 mr-2" />
                分享到社区
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
